import { QuestionType, Bucket, ResultsProps } from '@mightybyte/business';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useMemo } from 'react';
import { CustomCheckbox } from '@/components/uiWrappers/checkbox.tsx';

export type { ResultsProps };

interface BucketConfigInputProps {
  questionType: QuestionType;
  value: ResultsProps | null;
  onChange: (value: ResultsProps | null) => void;
  choiceOptions?: string[]; // For SingleChoice questions
  isBucketQuestion: boolean;
  onBucketChecked: (v:boolean)=>void
}

const bucketLabels = {
  [Bucket.One]: 'Bucket 1',
  [Bucket.Two]: 'Bucket 2',
};

export function BucketConfigInput({ questionType, value, onChange, choiceOptions = [], onBucketChecked, isBucketQuestion }: BucketConfigInputProps) {
  // Initialize default values when bucket question is checked
  const handleBucketCheckChange = (checked: boolean) => {
    onBucketChecked(checked);

    if (checked && !value) {
      // Auto-initialize default bucket configuration based on question type
      if (questionType === QuestionType.Boolean) {
        onChange({
          type: QuestionType.Boolean,
          props: {
            trueValue: Bucket.One,
            falseValue: Bucket.Two,
          },
        });
      } else if (questionType === QuestionType.Numeric) {
        onChange({
          type: QuestionType.Numeric,
          props: {
            threshold: 0,
            belowThreshold: Bucket.One,
            aboveOrEqualThreshold: Bucket.Two,
          },
        });
      } else if (questionType === QuestionType.Percentage) {
        onChange({
          type: QuestionType.Percentage,
          props: {
            threshold: 50,
            belowThreshold: Bucket.One,
            aboveOrEqualThreshold: Bucket.Two,
          },
        });
      } else if (questionType === QuestionType.SingleChoice) {
        // Initialize with first option mapping to Bucket.One
        const filteredOptions = choiceOptions.filter(opt => opt.trim());
        const optionToBucketMap: { [option: string]: Bucket } = {};
        filteredOptions.forEach(option => {
          optionToBucketMap[option] = Bucket.One;
        });
        onChange({
          type: QuestionType.SingleChoice,
          props: {
            optionToBucketMap,
          },
        });
      }
    }
  };

  // Boolean question configuration
  const handleBooleanChange = (field: 'trueValue' | 'falseValue', bucket: Bucket) => {
    const currentProps = value?.type === QuestionType.Boolean ? value.props : { trueValue: Bucket.One, falseValue: Bucket.Two };
    onChange({
      type: QuestionType.Boolean,
      props: {
        ...currentProps,
        [field]: bucket,
      },
    });
  };

  // Numeric/Percentage question configuration
  const handleThresholdChange = (
    field: 'threshold' | 'belowThreshold' | 'aboveOrEqualThreshold',
    newValue: string | Bucket
  ) => {
    if (questionType === QuestionType.Numeric) {
      const currentProps = value?.type === QuestionType.Numeric
        ? value.props
        : { threshold: 0, belowThreshold: Bucket.One, aboveOrEqualThreshold: Bucket.Two };

      onChange({
        type: QuestionType.Numeric,
        props: {
          ...currentProps,
          [field]: field === 'threshold' ? (newValue === '' ? 0 : parseFloat(newValue as string)) : newValue,
        },
      });
    } else if (questionType === QuestionType.Percentage) {
      const currentProps = value?.type === QuestionType.Percentage
        ? value.props
        : { threshold: 50, belowThreshold: Bucket.One, aboveOrEqualThreshold: Bucket.Two };

      onChange({
        type: QuestionType.Percentage,
        props: {
          ...currentProps,
          [field]: field === 'threshold' ? (newValue === '' ? 0 : parseFloat(newValue as string)) : newValue,
        },
      });
    }
  };

  // Single choice question configuration
  const handleSingleChoiceChange = (option: string, bucket: Bucket) => {
    const currentProps = value?.type === QuestionType.SingleChoice ? value.props : { optionToBucketMap: {} };
    onChange({
      type: QuestionType.SingleChoice,
      props: {
        optionToBucketMap: {
          ...currentProps.optionToBucketMap,
          [option]: bucket,
        },
      },
    });
  };

  const currentThreshold = useMemo(() => {
    if (questionType === QuestionType.Numeric && value?.type === QuestionType.Numeric) {
      return value.props.threshold;
    }
    if (questionType === QuestionType.Percentage && value?.type === QuestionType.Percentage) {
      return value.props.threshold;
    }
    return questionType === QuestionType.Percentage ? 50 : 0;
  }, [questionType, value]);

  const currentBelowBucket = useMemo(() => {
    if (questionType === QuestionType.Numeric && value?.type === QuestionType.Numeric) {
      return value.props.belowThreshold;
    }
    if (questionType === QuestionType.Percentage && value?.type === QuestionType.Percentage) {
      return value.props.belowThreshold;
    }
    return Bucket.One;
  }, [questionType, value]);

  const currentAboveBucket = useMemo(() => {
    if (questionType === QuestionType.Numeric && value?.type === QuestionType.Numeric) {
      return value.props.aboveOrEqualThreshold;
    }
    if (questionType === QuestionType.Percentage && value?.type === QuestionType.Percentage) {
      return value.props.aboveOrEqualThreshold;
    }
    return Bucket.Two;
  }, [questionType, value]);

  return (
    <div className="space-y-3 bg-primary-soft p-4 rounded-lg">
      <div className="space-y-3">
        <div className="flex items-center">
          <CustomCheckbox
              id="isBucketQuestion"
              checked={isBucketQuestion}
              onCheckedChange={handleBucketCheckChange}
          />
          <Label htmlFor="isBucketQuestion" className="cursor-pointer pl-2">
            Use as Bucket Question
          </Label>
        </div>
        <p className="text-xs text-muted-foreground">
          Only one question per quiz can be a bucket question. Users will be split into different buckets based on their answer.
        </p>
      </div>
      {isBucketQuestion && <div>
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-foreground tracking-wide">Bucket Configuration</h3>
        </div>

        {questionType === QuestionType.Boolean && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>True Answer → Bucket</Label>
                  <Select
                      value={value?.type === QuestionType.Boolean ? value.props.trueValue : Bucket.One}
                      onValueChange={(val) => handleBooleanChange('trueValue', val as Bucket)}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={Bucket.One}>{bucketLabels[Bucket.One]}</SelectItem>
                      <SelectItem value={Bucket.Two}>{bucketLabels[Bucket.Two]}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>False Answer → Bucket</Label>
                  <Select
                      value={value?.type === QuestionType.Boolean ? value.props.falseValue : Bucket.Two}
                      onValueChange={(val) => handleBooleanChange('falseValue', val as Bucket)}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={Bucket.One}>{bucketLabels[Bucket.One]}</SelectItem>
                      <SelectItem value={Bucket.Two}>{bucketLabels[Bucket.Two]}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
        )}

        {(questionType === QuestionType.Numeric || questionType === QuestionType.Percentage) && (
            <div className="space-y-3">
              <div>
                <Label>Threshold Value</Label>
                <Input
                    type="number"
                    min={0}
                    max={questionType === QuestionType.Percentage ? 100 : undefined}
                    value={currentThreshold}
                    onChange={(e) => handleThresholdChange('threshold', e.target.value)}
                    placeholder="Enter threshold"
                    className="h-9"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Below or Equal → Bucket</Label>
                  <Select
                      value={currentBelowBucket}
                      onValueChange={(val) => handleThresholdChange('belowThreshold', val as Bucket)}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={Bucket.One}>{bucketLabels[Bucket.One]}</SelectItem>
                      <SelectItem value={Bucket.Two}>{bucketLabels[Bucket.Two]}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Above Threshold → Bucket</Label>
                  <Select
                      value={currentAboveBucket}
                      onValueChange={(val) => handleThresholdChange('aboveOrEqualThreshold', val as Bucket)}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={Bucket.One}>{bucketLabels[Bucket.One]}</SelectItem>
                      <SelectItem value={Bucket.Two}>{bucketLabels[Bucket.Two]}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
        )}

        {questionType === QuestionType.SingleChoice && (
            <div className="space-y-2">
              <Label>Assign each option to a bucket</Label>
              {choiceOptions.filter(opt => opt.trim()).map((option, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <span className="flex-1 text-sm truncate">{option}</span>
                    <Select
                        value={
                          value?.type === QuestionType.SingleChoice
                              ? value.props.optionToBucketMap[option] || Bucket.One
                              : Bucket.One
                        }
                        onValueChange={(val) => handleSingleChoiceChange(option, val as Bucket)}
                    >
                      <SelectTrigger className="h-9 w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={Bucket.One}>{bucketLabels[Bucket.One]}</SelectItem>
                        <SelectItem value={Bucket.Two}>{bucketLabels[Bucket.Two]}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
              ))}
            </div>
        )}
      </div>}

    </div>
  );
}
